import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
} from '@mui/material';
import { RotateCcw } from 'lucide-react';
import { usePayments } from '../../hooks/payment/usePayments';
import type { Invoice, RefundReason } from '../../types/payment';

interface RefundDialogProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice;
  onSuccess: () => void;
}

export const RefundDialog: React.FC<RefundDialogProps> = ({
  open,
  onClose,
  invoice,
  onSuccess,
}) => {
  const { requestRefund } = usePayments();

  const maxAmountDollars = invoice.totalCents / 100;

  const [refundType, setRefundType] = useState<'full' | 'partial'>('full');
  const [amount, setAmount] = useState<string>(maxAmountDollars.toFixed(2));
  const [reason, setReason] = useState<RefundReason>('CUSTOMER_REQUEST');
  const [description, setDescription] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountCents = Math.round(parseFloat(amount || '0') * 100);
  const isAmountValid = amountCents > 0 && amountCents <= invoice.totalCents;

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: invoice.currency,
    }).format(cents / 100);
  };

  const customerName = invoice.customer
    ? `${invoice.customer.firstName} ${invoice.customer.lastName || ''}`.trim()
    : 'N/A';

  const handleRefundTypeChange = (type: 'full' | 'partial') => {
    setRefundType(type);
    if (type === 'full') {
      setAmount(maxAmountDollars.toFixed(2));
    }
  };

  const handleSubmit = async () => {
    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    try {
      setProcessing(true);
      setError(null);
      await requestRefund(invoice.id.toString(), {
        amountCents,
        reason,
      });
      handleClose();
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to process refund');
      setShowConfirmation(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setRefundType('full');
    setAmount(maxAmountDollars.toFixed(2));
    setReason('CUSTOMER_REQUEST');
    setDescription('');
    setShowConfirmation(false);
    setError(null);
    setProcessing(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <RotateCcw size={20} />
          <Typography variant="h6">
            {showConfirmation ? 'Confirm Refund' : 'Issue Refund'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {showConfirmation ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="warning">
              Are you sure you want to refund {formatCurrency(amountCents)} for invoice{' '}
              {invoice.invoiceNumber}? This action cannot be undone.
            </Alert>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Invoice
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {invoice.invoiceNumber}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body2">{customerName}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Refund Amount
                </Typography>
                <Typography variant="body2" fontWeight={600} color="error">
                  {formatCurrency(amountCents)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Reason
                </Typography>
                <Typography variant="body2">
                  {reason
                    .replace(/_/g, ' ')
                    .toLowerCase()
                    .replace(/\b\w/g, c => c.toUpperCase())}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {/* Invoice Info */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Invoice
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {invoice.invoiceNumber}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body2">{customerName}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Invoice Total
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(invoice.totalCents)}
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* Refund Type */}
            <RadioGroup
              value={refundType}
              onChange={e => handleRefundTypeChange(e.target.value as 'full' | 'partial')}
            >
              <FormControlLabel value="full" control={<Radio />} label="Full refund" />
              <FormControlLabel value="partial" control={<Radio />} label="Partial refund" />
            </RadioGroup>

            {/* Amount */}
            <TextField
              label="Refund Amount"
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              disabled={refundType === 'full'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {invoice.currency === 'aud' || invoice.currency === 'AUD'
                      ? '$'
                      : invoice.currency}
                  </InputAdornment>
                ),
              }}
              inputProps={{
                min: 0.01,
                max: maxAmountDollars,
                step: 0.01,
              }}
              error={!isAmountValid && amount !== ''}
              helperText={
                !isAmountValid && amount !== ''
                  ? `Amount must be between $0.01 and ${formatCurrency(invoice.totalCents)}`
                  : undefined
              }
              fullWidth
            />

            {/* Reason */}
            <FormControl fullWidth>
              <InputLabel>Reason</InputLabel>
              <Select
                value={reason}
                label="Reason"
                onChange={e => setReason(e.target.value as RefundReason)}
              >
                <MenuItem value="CUSTOMER_REQUEST">Customer Request</MenuItem>
                <MenuItem value="DUPLICATE">Duplicate</MenuItem>
                <MenuItem value="FRAUDULENT">Fraudulent</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </FormControl>

            {/* Description (shown for OTHER reason) */}
            {reason === 'OTHER' && (
              <TextField
                label="Description (optional)"
                value={description}
                onChange={e => setDescription(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={showConfirmation ? () => setShowConfirmation(false) : handleClose}>
          {showConfirmation ? 'Back' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          color={showConfirmation ? 'error' : 'primary'}
          onClick={handleSubmit}
          disabled={!isAmountValid || processing}
          startIcon={processing ? <CircularProgress size={16} /> : <RotateCcw size={16} />}
        >
          {processing ? 'Processing...' : showConfirmation ? 'Confirm Refund' : 'Review Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
